import { isDefined } from "@clipboard-health/util-ts";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { pendingSettingsSchema } from "@src/appV2/FacilityDrawer/BillingAlerts/types";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { z } from "zod";

export const mandatoryBreakConfigurationSchema = z.object({
  breakDurationInMinutes: z.number(),
});

export const useGetFacilityProfileResponseSchema = z.object({
  name: z.string(),
  phone: z.string().nullish(),
  userId: z.string(),
  quizWorkersOnRules: z
    .custom<boolean | undefined>((value) => {
      if (value === null || value === undefined) {
        return true;
      }

      return typeof value === "boolean";
    })
    .transform((value) => (value === null ? undefined : value)),
  maxAllowedWorkHoursPerWeek: z.number().nullish(),
  maxAllowedWorkConsecutiveHours: z.number().nullish(),
  pendingSettings: pendingSettingsSchema.nullish(),
  tmz: z.string().nullish(),
  status: z.string().nullish(),
  variableCharge: z
    .object({
      enabled: z.boolean().optional(),
    })
    .nullish(),
  placementSettings: z
    .object({
      managedByParentWorkplace: z.boolean().optional(),
      isProspect: z.boolean().optional(),
    })
    .optional(),
  parentFacilityId: z.string().nullish(),
  mandatoryBreakConfiguration: mandatoryBreakConfigurationSchema.optional(),
  fullAddress: z
    .object({
      stateCode: z.string().optional(),
      state: z.string().optional(),
      city: z.string().optional(),
      postalCode: z.string().optional(),
      formatted: z.string().optional(),
      streetNumber: z.string().optional(),
      streetName: z.string().optional(),
      region: z.string().optional(),
      country: z.string().optional(),
      countryCode: z.string().optional(),
    })
    .optional(),
  isRequestToBookPerShiftEnabled: z.boolean().optional(),
  shiftFeedbackQrCodeConfiguration: z
    .object({
      pinValidationEnabled: z.boolean().optional(),
    })
    .optional(),
  rates: z.record(z.string(), z.number()).optional(),
});

export type UseGetFacilityProfileResponse = z.infer<typeof useGetFacilityProfileResponseSchema>;

export interface UseGetFacilityProfileOptions
  extends UseGetQueryOptions<UseGetFacilityProfileResponse> {
  customHeaders?: Record<string, string>;
}

export function useGetFacilityProfile(
  facilityId: string,
  projection?: string,
  options: UseGetFacilityProfileOptions = {}
) {
  const searchParams = new URLSearchParams();
  if (isDefined(projection)) {
    searchParams.set("projection", projection);
  }

  return useGetQuery({
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/facilityprofile/${facilityId}?${searchParams}`,
    responseSchema: useGetFacilityProfileResponseSchema,
    meta: {
      logErrorMessage: `${APP_V2_APP_EVENTS.GET_FACILITY_PROFILE_FAILED}`,
    },
    ...options,
  });
}
